import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Popover,
  Popconfirm,
  Row,
  Select,
  Slider,
  Switch,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import moment from 'moment-timezone';
import L from 'leaflet';
import { Circle, Map as LeafletMap, Marker, Polygon, Polyline, TileLayer, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ensureStarterTemplates,
  fetchAllAssets,
  fetchAssetsForDate,
  fetchTemplates,
  getFlightBayMappings,
  importFlightScheduleForTemplate,
  listFlightImports,
  fetchUtilizationTrack,
  fetchUtilizationEvents,
  fetchUtilizationReport,
  runUtilizationCompute,
  saveTemplate,
  upsertFlightBayMappings,
} from './utils/utilizationService';
import { METRIC_TYPES, formatDuration } from './utils/utilizationDefaults';
import { normalizeBayKey } from './utils/flightContextEngine';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const METRIC_UNIT_FALLBACK = {
  carry_ignition_time: 'sec',
  carry_distance_run: 'km',
  carry_fuel_consumed: 'ltr',
};

const metricUnit = (metricType, comparisonUnit) => comparisonUnit || METRIC_UNIT_FALLBACK[metricType] || 'count';

const formatMetricValue = (value, unit) => {
  if (value === null || value === undefined) return '-';
  if (unit === 'sec') return formatDuration(value);
  if (unit === 'km' || unit === 'ltr') return Number(value).toFixed(2);
  return Number(value).toLocaleString();
};

const renderTickCross = (value) => {
  if (value === true) return <span style={{ color: '#52c41a', fontWeight: 700 }}>✓</span>;
  if (value === false) return <span style={{ color: '#ff4d4f', fontWeight: 700 }}>✗</span>;
  return <span style={{ color: '#8c8c8c' }}>-</span>;
};

const polygonCentroid = (coordinates = []) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return null;
  const sum = coordinates.reduce((acc, p) => ({ lat: acc.lat + Number(p.lat || 0), lng: acc.lng + Number(p.lng || 0) }), { lat: 0, lng: 0 });
  return {
    lat: sum.lat / coordinates.length,
    lng: sum.lng / coordinates.length,
  };
};

const resolveAssetTypeKey = (asset) => {
  return (
    asset?.assetSubType?._id
    || asset?.asset_sub_type?._id
    || asset?.assetType?._id
    || asset?.asset_type?._id
    || asset?.assetSubTypeId
    || asset?.assetTypeId
    || asset?.assetSubTypeName
    || asset?.assetTypeName
    || asset?.assetSubType
    || asset?.assetType
    || asset?.type
    || ''
  ).toString().trim();
};

const resolveMotionState = (point) => {
  if (!point) return 'STOP';
  const raw = point.raw || {};
  const ignitionRaw = Number(raw.calculated_ignition ?? raw.effective_ignition ?? point.ignition ?? 0);
  const idleRaw = Number(raw.calculated_idle ?? raw.effective_idle ?? point.idle ?? 0);
  if (ignitionRaw <= 0) return 'STOP';
  if (idleRaw > 0) return 'PAUSE';
  return 'PLAY';
};

const extractZoneTokens = (zoneName) => {
  const text = `${zoneName || ''}`.toUpperCase();
  const matches = text.match(/\b\d{1,3}[A-Z]?\b/g) || [];
  return Array.from(new Set(matches.map((token) => normalizeBayKey(token)).filter(Boolean)));
};

const autoZoneCandidatesForBay = (bayKey, zones) => {
  const key = normalizeBayKey(bayKey);
  if (!key) return [];
  const candidates = (zones || []).filter((zone) => extractZoneTokens(zone?.name).includes(key));
  return candidates.map((zone) => zone.id);
};

const csvEscape = (value) => {
  const str = `${value ?? ''}`;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const downloadCSV = (filename, headers, rows) => {
  const csv = [
    headers.map(csvEscape).join(','),
    ...rows.map((row) => row.map(csvEscape).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const normalizeTemplate = (template) => ({
  ...template,
  zones: Array.isArray(template.zones) ? template.zones : [],
  metrics: Array.isArray(template.metrics) ? template.metrics : [],
  rules: {
    sampleIntervalSec: 10,
    maxGapSamples: 3,
    debouncePoints: 2,
    dwellThresholdSec: 120,
    tripMaxDurationSec: 8 * 60 * 60,
    proximityEnabled: false,
    defaultProximityCutoffMeters: 30,
    proximityExitHysteresisPct: 20,
    proximityByAssetType: {},
    proximityByAssetSubType: {},
    zonePriority: {},
    ...template.rules,
  },
  scope: {
    assetIds: [],
    assetTypes: [],
    assetSubTypes: [],
    assetModels: [],
    locations: [],
    subLocations: [],
    ...template.scope,
  },
});

function AssetUtilizationStudio() {
  const [templates, setTemplates] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateDraft, setTemplateDraft] = useState(null);
  const [selectedDate, setSelectedDate] = useState(moment());

  const [loadingInit, setLoadingInit] = useState(true);
  const [computing, setComputing] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);

  const [report, setReport] = useState(null);
  const [dashboardTab, setDashboardTab] = useState('utilization');
  const [selectedAssetFilter, setSelectedAssetFilter] = useState([]);
  const [flightConfigLoading, setFlightConfigLoading] = useState(false);
  const [flightImporting, setFlightImporting] = useState(false);
  const [flightImportFile, setFlightImportFile] = useState(null);
  const [flightImportTimezone, setFlightImportTimezone] = useState('');
  const [flightImportSummary, setFlightImportSummary] = useState(null);
  const [flightImportsData, setFlightImportsData] = useState({ rows: [], batches: [], summary: null });
  const [flightManualMappings, setFlightManualMappings] = useState([]);
  const [savingFlightMappings, setSavingFlightMappings] = useState(false);

  const [zoneModalVisible, setZoneModalVisible] = useState(false);
  const [newZone, setNewZone] = useState({
    name: '',
    isBase: false,
    coordinatesText: '',
  });
  const [zoneMapModalVisible, setZoneMapModalVisible] = useState(false);
  const [zoneMapPreviewZoneId, setZoneMapPreviewZoneId] = useState('all');

  const [metricModalVisible, setMetricModalVisible] = useState(false);
  const [newMetric, setNewMetric] = useState({
    label: '',
    type: 'zone_entries',
    sourceZoneId: undefined,
    destinationZoneId: undefined,
    zoneId: undefined,
    baseZoneId: undefined,
  });
  const [geoImportModalVisible, setGeoImportModalVisible] = useState(false);
  const [geoImportUrl, setGeoImportUrl] = useState('https://bird-group.theassetpro.com/geo/ahm.geojson');
  const [geoImportFile, setGeoImportFile] = useState(null);
  const [geoImportAsBase, setGeoImportAsBase] = useState(false);
  const [geoImportLoading, setGeoImportLoading] = useState(false);

  const [eventsDrawerVisible, setEventsDrawerVisible] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsData, setEventsData] = useState([]);
  const [eventsAsset, setEventsAsset] = useState(null);
  const [trackPoints, setTrackPoints] = useState([]);
  const [trackLoading, setTrackLoading] = useState(false);
  const [selectedMapEvent, setSelectedMapEvent] = useState(null);
  const [rangeStartEventId, setRangeStartEventId] = useState(null);
  const [rangeEndEventId, setRangeEndEventId] = useState(null);
  const [isMapPlaying, setIsMapPlaying] = useState(false);
  const [isDrawerFullWidth, setIsDrawerFullWidth] = useState(false);
  const [playbackIndex, setPlaybackIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.5);
  const [eventChannelFilter, setEventChannelFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState([]);
  const [matrixMode, setMatrixMode] = useState('polygon');
  const mapRef = useRef(null);
  const zoneMapRef = useRef(null);
  const playbackSpeedRef = useRef(1.5);
  const playbackLastTickMsRef = useRef(0);
  const playbackCarryRef = useRef(0);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const availableTimezones = useMemo(() => {
    return moment.tz.names().slice(0, 1000);
  }, []);

  const assetTypeOptions = useMemo(() => {
    const map = new Map();
    (assets || []).forEach((asset) => {
      const key = (
        asset?.assetSubType?._id
        || asset?.asset_sub_type?._id
        || asset?.assetType?._id
        || asset?.asset_type?._id
        || asset?.assetSubTypeId
        || asset?.assetTypeId
        || asset?.assetSubTypeName
        || asset?.assetTypeName
        || asset?.assetSubType
        || asset?.assetType
        || asset?.type
        || ''
      ).toString().trim();

      if (!key) return;

      const label = (
        asset?.assetSubType?.name
        || asset?.asset_sub_type?.name
        || asset?.assetType?.name
        || asset?.asset_type?.name
        || asset?.assetSubTypeName
        || asset?.assetTypeName
        || key
      ).toString();

      if (!map.has(key)) map.set(key, { key, label });
    });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [assets]);

  const currentDrawerAsset = useMemo(
    () => assets.find((a) => a._id === eventsAsset?.assetId) || null,
    [assets, eventsAsset]
  );

  const currentServiceCutoffMeters = useMemo(() => {
    const rules = selectedTemplate?.rules || {};
    const key = resolveAssetTypeKey(currentDrawerAsset);
    const fromType = key ? Number(rules.proximityByAssetType?.[key]) : NaN;
    if (Number.isFinite(fromType) && fromType > 0) return fromType;
    const fallback = Number(rules.defaultProximityCutoffMeters || 30);
    return Number.isFinite(fallback) && fallback > 0 ? fallback : 30;
  }, [selectedTemplate, currentDrawerAsset]);

  const flightManualMapByKey = useMemo(() => {
    const map = {};
    (flightManualMappings || []).forEach((item) => {
      const key = normalizeBayKey(item?.bayKey);
      if (!key) return;
      map[key] = item;
    });
    return map;
  }, [flightManualMappings]);

  const allImportedBayKeys = useMemo(() => {
    return Array.from(new Set((flightImportsData?.rows || []).flatMap((row) => row.bayKeys || []))).sort();
  }, [flightImportsData]);

  const flightBayMappingRows = useMemo(() => {
    const zones = templateDraft?.zones || selectedTemplate?.zones || [];
    const zoneNameById = {};
    zones.forEach((zone) => { zoneNameById[zone.id] = zone.name || zone.id; });

    return allImportedBayKeys.map((bayKey) => {
      const manual = flightManualMapByKey[bayKey];
      const manualZoneId = manual?.zoneId || null;
      const autoZoneIds = autoZoneCandidatesForBay(bayKey, zones);
      const effectiveZoneId = manualZoneId || (autoZoneIds.length === 1 ? autoZoneIds[0] : null);
      const status = effectiveZoneId ? 'mapped' : (autoZoneIds.length > 1 ? 'ambiguous' : 'unmapped');
      return {
        key: bayKey,
        bayKey,
        autoZoneIds,
        autoZoneNames: autoZoneIds.map((zoneId) => zoneNameById[zoneId] || zoneId),
        manualZoneId,
        effectiveZoneId,
        effectiveZoneName: effectiveZoneId ? (zoneNameById[effectiveZoneId] || effectiveZoneId) : '-',
        status,
      };
    });
  }, [allImportedBayKeys, flightManualMapByKey, selectedTemplate, templateDraft]);

  const flightContext = report?.flightContext || null;

  const timelineEventsData = useMemo(() => {
    const baseEvents = Array.isArray(eventsData) ? [...eventsData] : [];
    const points = Array.isArray(trackPoints) ? trackPoints : [];
    if (points.length === 0) return baseEvents;

    const timelineStart = Number(points[0].ts);
    const timelineEnd = Number(points[points.length - 1].ts);
    if (!Number.isFinite(timelineStart) || !Number.isFinite(timelineEnd) || timelineEnd <= timelineStart) {
      return baseEvents;
    }

    const occupancyEvents = baseEvents
      .filter((e) => ['ENTER', 'EXIT', 'SERVICE_ENTER', 'SERVICE_EXIT'].includes(e.eventType))
      .filter((e) => Number.isFinite(Number(e.startTs)))
      .sort((a, b) => Number(a.startTs) - Number(b.startTs));

    const byTime = {};
    occupancyEvents.forEach((event) => {
      const key = Number(event.startTs);
      if (!byTime[key]) byTime[key] = [];
      byTime[key].push(event);
    });

    const checkpointSet = new Set([timelineStart, timelineEnd]);
    Object.keys(byTime).forEach((k) => checkpointSet.add(Number(k)));
    const checkpoints = Array.from(checkpointSet)
      .filter((v) => Number.isFinite(v))
      .sort((a, b) => a - b);

    const activePolygonZones = new Set();
    const activeServiceZones = new Set();
    const outsideEvents = [];

    for (let i = 0; i < checkpoints.length - 1; i += 1) {
      const t = checkpoints[i];
      const nextT = checkpoints[i + 1];
      const atT = byTime[t] || [];

      atT.forEach((ev) => {
        if (ev.eventType === 'ENTER' && ev.zoneId) activePolygonZones.add(ev.zoneId);
        if (ev.eventType === 'EXIT' && ev.zoneId) activePolygonZones.delete(ev.zoneId);
        if (ev.eventType === 'SERVICE_ENTER' && ev.zoneId) activeServiceZones.add(ev.zoneId);
        if (ev.eventType === 'SERVICE_EXIT' && ev.zoneId) activeServiceZones.delete(ev.zoneId);
      });

      const occupied = activePolygonZones.size > 0 || activeServiceZones.size > 0;
      const durationSec = Math.max(0, Math.round((nextT - t) / 1000));
      if (!occupied && durationSec > 0) {
        outsideEvents.push({
          id: `OUTSIDE_${eventsAsset?.assetId || 'asset'}_${t}_${nextT}`,
          eventType: 'OUTSIDE',
          templateId: selectedTemplateId,
          orgId: selectedTemplate?.orgId,
          assetId: eventsAsset?.assetId,
          assetCode: eventsAsset?.assetCode,
          date: moment(selectedDate).format('YYYY-MM-DD'),
          zoneId: null,
          zoneName: 'Outside Polygon/Service',
          startTs: t,
          endTs: nextT,
          durationSec,
          payloadJson: { detectionMode: 'outside', source: 'derived_timeline' },
        });
      }
    }

    return [...baseEvents, ...outsideEvents].sort((a, b) => Number(a.startTs || 0) - Number(b.startTs || 0));
  }, [eventsData, trackPoints, eventsAsset, selectedDate, selectedTemplateId, selectedTemplate]);

  const playbackSelectableEvents = useMemo(() => (
    timelineEventsData
      .filter((e) => e && Number.isFinite(Number(e.startTs)))
      .sort((a, b) => Number(a.startTs) - Number(b.startTs))
  ), [timelineEventsData]);

  const selectedRangeEvents = useMemo(() => {
    const byId = {};
    playbackSelectableEvents.forEach((e) => { byId[e.id] = e; });
    const startEvent = rangeStartEventId ? byId[rangeStartEventId] : null;
    const endEvent = rangeEndEventId ? byId[rangeEndEventId] : null;
    if (!startEvent || !endEvent) return null;

    const startTs = Number(startEvent.startTs);
    const rawEndTs = Number(endEvent.endTs || endEvent.startTs);
    const endTs = Number.isFinite(rawEndTs) ? rawEndTs : Number(endEvent.startTs);
    if (!Number.isFinite(startTs) || !Number.isFinite(endTs)) return null;
    if (endTs < startTs) return null;

    return { startEvent, endEvent, startTs, endTs };
  }, [playbackSelectableEvents, rangeStartEventId, rangeEndEventId]);

  const mapPlaybackPoints = useMemo(() => {
    if (!Array.isArray(trackPoints) || trackPoints.length === 0) return [];
    if (selectedRangeEvents) {
      const points = trackPoints.filter((p) => p.ts >= selectedRangeEvents.startTs && p.ts <= selectedRangeEvents.endTs);
      if (points.length > 1) return points;
      return trackPoints.filter((p) => p.ts >= (selectedRangeEvents.startTs - 5 * 60 * 1000) && p.ts <= (selectedRangeEvents.endTs + 5 * 60 * 1000));
    }

    if (!selectedMapEvent) return trackPoints;

    const startTs = Number(selectedMapEvent.startTs || trackPoints[0].ts);
    const endTs = Number(selectedMapEvent.endTs || (startTs + 10 * 60 * 1000));
    const safeEnd = endTs > startTs ? endTs : (startTs + 10 * 60 * 1000);
    const points = trackPoints.filter((p) => p.ts >= startTs && p.ts <= safeEnd);
    if (points.length > 1) return points;

    const fallbackStart = startTs - (5 * 60 * 1000);
    const fallbackEnd = safeEnd + (5 * 60 * 1000);
    return trackPoints.filter((p) => p.ts >= fallbackStart && p.ts <= fallbackEnd);
  }, [trackPoints, selectedMapEvent, selectedRangeEvents]);

  const zoneMapPreviewZones = useMemo(() => {
    const zones = templateDraft?.zones || [];
    if (zoneMapPreviewZoneId === 'all') return zones;
    return zones.filter((zone) => zone.id === zoneMapPreviewZoneId);
  }, [templateDraft, zoneMapPreviewZoneId]);

  const zoneMapPreviewTitle = useMemo(() => {
    if (zoneMapPreviewZoneId === 'all') return 'All Geofences';
    const zone = (templateDraft?.zones || []).find((item) => item.id === zoneMapPreviewZoneId);
    return zone?.name || 'Geofence';
  }, [templateDraft, zoneMapPreviewZoneId]);

  useEffect(() => {
    playbackSpeedRef.current = Number(playbackSpeed || 1.5);
  }, [playbackSpeed]);

  useEffect(() => {
    if (!isMapPlaying) return undefined;
    if (!mapPlaybackPoints || mapPlaybackPoints.length <= 1) return undefined;

    playbackLastTickMsRef.current = Date.now();
    playbackCarryRef.current = 0;

    // Base playback at 1x ~= 2 points per second (same feel as old 500ms step)
    const basePointsPerSecAt1x = 2;
    const tickMs = 33;

    const timer = setInterval(() => {
      const now = Date.now();
      const elapsedMs = Math.max(1, now - playbackLastTickMsRef.current);
      playbackLastTickMsRef.current = now;

      const speed = Math.max(0.25, Number(playbackSpeedRef.current || 1.5));
      const pointsFloat = (elapsedMs / 1000) * basePointsPerSecAt1x * speed + playbackCarryRef.current;
      const pointsStep = Math.floor(pointsFloat);
      playbackCarryRef.current = pointsFloat - pointsStep;

      if (pointsStep <= 0) return;

      setPlaybackIndex((idx) => {
        if (idx >= mapPlaybackPoints.length - 1) {
          setIsMapPlaying(false);
          return idx;
        }
        const next = Math.min(mapPlaybackPoints.length - 1, idx + pointsStep);
        if (next >= mapPlaybackPoints.length - 1) setIsMapPlaying(false);
        return next;
      });
    }, tickMs);

    return () => {
      clearInterval(timer);
    };
  }, [isMapPlaying, mapPlaybackPoints]);

  useEffect(() => {
    setPlaybackIndex(0);
    if (selectedMapEvent || selectedRangeEvents) setIsMapPlaying(true);
  }, [selectedMapEvent, selectedRangeEvents, mapPlaybackPoints.length]);

  useEffect(() => {
    if (!mapRef.current) return;
    const mapInstance = mapRef.current.leafletElement || mapRef.current;
    if (!mapInstance) return;

    const boundsPoints = [];
    (selectedTemplate?.zones || []).forEach((zone) => {
      (zone.coordinates || []).forEach((p) => {
        if (Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng))) {
          boundsPoints.push([Number(p.lat), Number(p.lng)]);
        }
      });
    });
    mapPlaybackPoints.forEach((p) => boundsPoints.push([p.lat, p.lng]));

    if (boundsPoints.length >= 2) {
      mapInstance.fitBounds(L.latLngBounds(boundsPoints), { padding: [24, 24] });
    }
  }, [selectedTemplate, mapPlaybackPoints, selectedMapEvent]);

  useEffect(() => {
    if (!isMapPlaying) return;
    if (!mapRef.current) return;
    const mapInstance = mapRef.current.leafletElement || mapRef.current;
    if (!mapInstance) return;
    if (!mapPlaybackPoints || mapPlaybackPoints.length === 0) return;

    if (mapPlaybackPoints.length === 1) {
      const point = mapPlaybackPoints[0];
      mapInstance.setView([point.lat, point.lng], Math.max(mapInstance.getZoom(), 18), { animate: true });
      return;
    }

    const bounds = L.latLngBounds(mapPlaybackPoints.map((p) => [p.lat, p.lng]));
    mapInstance.fitBounds(bounds, {
      padding: [36, 36],
      maxZoom: 18,
      animate: true,
    });
  }, [isMapPlaying, mapPlaybackPoints]);

  useEffect(() => {
    if (!zoneMapModalVisible) return;
    if (!zoneMapRef.current) return;
    const mapInstance = zoneMapRef.current.leafletElement || zoneMapRef.current;
    if (!mapInstance) return;

    const boundsPoints = [];
    zoneMapPreviewZones.forEach((zone) => {
      (zone.coordinates || []).forEach((p) => {
        if (Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng))) {
          boundsPoints.push([Number(p.lat), Number(p.lng)]);
        }
      });
    });

    setTimeout(() => {
      mapInstance.invalidateSize();
      if (boundsPoints.length >= 2) {
        mapInstance.fitBounds(L.latLngBounds(boundsPoints), { padding: [24, 24], maxZoom: 18 });
      } else if (boundsPoints.length === 1) {
        mapInstance.setView(boundsPoints[0], 18);
      }
    }, 80);
  }, [zoneMapModalVisible, zoneMapPreviewZones]);

  const openZoneMapPreview = (zoneId = 'all') => {
    setZoneMapPreviewZoneId(zoneId);
    setZoneMapModalVisible(true);
  };

  useEffect(() => {
    const init = async () => {
      setLoadingInit(true);
      try {
        ensureStarterTemplates();
        const [tpls, assetsResp] = await Promise.all([
          fetchTemplates(),
          fetchAssetsForDate({ date: moment() }),
        ]);
        const normalized = tpls.map(normalizeTemplate);
        setTemplates(normalized);
        setAssets(Array.isArray(assetsResp) ? assetsResp : []);

        if (normalized.length > 0) {
          setSelectedTemplateId(normalized[0].id);
          setTemplateDraft(JSON.parse(JSON.stringify(normalized[0])));
        }
      } catch (error) {
        message.error(`Failed to initialize utilization studio: ${error.message}`);
      } finally {
        setLoadingInit(false);
      }
    };

    init();
  }, []);

  useEffect(() => {
    const loadAssetsForDate = async () => {
      try {
        const dateBased = await fetchAssetsForDate({ date: selectedDate, templateId: selectedTemplateId });
        setAssets(Array.isArray(dateBased) ? dateBased : []);
      } catch (e) {
        const fallback = await fetchAllAssets();
        setAssets(Array.isArray(fallback) ? fallback : []);
      }
    };
    loadAssetsForDate();
  }, [selectedDate, selectedTemplateId]);

  useEffect(() => {
    if (selectedTemplate) {
      setTemplateDraft(JSON.parse(JSON.stringify(selectedTemplate)));
      setReport(null);
      setFlightImportTimezone(selectedTemplate.timezone || moment.tz.guess() || 'Asia/Kolkata');
    }
  }, [selectedTemplate]);

  const refreshFlightConfig = async (templateId = selectedTemplateId) => {
    if (!templateId) {
      setFlightImportsData({ rows: [], batches: [], summary: null });
      setFlightManualMappings([]);
      return;
    }

    setFlightConfigLoading(true);
    try {
      const [importsResp, mappingsResp] = await Promise.all([
        listFlightImports({ templateId }),
        getFlightBayMappings({ templateId }),
      ]);

      setFlightImportsData(importsResp.success ? (importsResp.data || { rows: [], batches: [], summary: null }) : { rows: [], batches: [], summary: null });
      setFlightManualMappings(mappingsResp.success ? (mappingsResp.data || []) : []);
    } finally {
      setFlightConfigLoading(false);
    }
  };

  useEffect(() => {
    refreshFlightConfig(selectedTemplateId);
  }, [selectedTemplateId]);

  const refreshTemplates = async (preferredTemplateId) => {
    const tpls = await fetchTemplates();
    const normalized = tpls.map(normalizeTemplate);
    setTemplates(normalized);
    const nextId = preferredTemplateId || (normalized[0] ? normalized[0].id : null);
    setSelectedTemplateId(nextId);
  };

  const loadReport = async (templateId = selectedTemplateId, date = selectedDate, assetIds = selectedAssetFilter) => {
    if (!templateId) return;
    setLoadingReport(true);
    try {
      const dateKey = moment(date).format('YYYY-MM-DD');
      const response = await fetchUtilizationReport({
        templateId,
        dateFrom: dateKey,
        dateTo: dateKey,
        assetIds,
      });
      if (!response.success) {
        message.error(response.error || 'Failed to load utilization report');
        return;
      }
      setReport(response.data);
    } finally {
      setLoadingReport(false);
    }
  };

  const handleCompute = async () => {
    if (!selectedTemplateId) {
      message.error('Please select a template');
      return;
    }

    setComputing(true);
    try {
      // Ensure compute always runs on latest template edits (zones/metrics/rules)
      if (templateDraft && templateDraft.id === selectedTemplateId) {
        await saveTemplate(templateDraft);
      }

      const computeResult = await runUtilizationCompute({
        templateId: selectedTemplateId,
        date: selectedDate,
        assetIds: selectedAssetFilter,
      });

      if (!computeResult.success) {
        message.error(computeResult.error || 'Failed to compute utilization');
        return;
      }

      const cappedNote = computeResult?.data?.capped
        ? `, processed ${computeResult.data.assetCount}/${computeResult.data.requestedAssetCount} assets (cap applied)`
        : '';
      message.success(`Utilization compute completed (${computeResult.source}${cappedNote})`);
      await loadReport(selectedTemplateId, selectedDate, selectedAssetFilter);
    } catch (error) {
      message.error(`Compute failed: ${error.message}`);
    } finally {
      setComputing(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateDraft) return;
    try {
      const saved = await saveTemplate(templateDraft);
      message.success(`Template saved: ${saved.name}`);
      await refreshTemplates(saved.id);
    } catch (error) {
      message.error(`Failed to save template: ${error.message}`);
    }
  };

  const upsertManualBayMappingLocal = (bayKey, zoneId) => {
    const normalizedBayKey = normalizeBayKey(bayKey);
    if (!normalizedBayKey) return;

    setFlightManualMappings((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const idx = list.findIndex((item) => normalizeBayKey(item?.bayKey) === normalizedBayKey);

      if (idx >= 0) {
        list[idx] = {
          ...list[idx],
          bayKey: normalizedBayKey,
          zoneId: zoneId || null,
          method: zoneId ? 'manual' : 'auto',
          confidence: zoneId ? 'high' : 'low',
          updatedAt: new Date().toISOString(),
        };
      } else {
        list.push({
          bayKey: normalizedBayKey,
          zoneId: zoneId || null,
          method: zoneId ? 'manual' : 'auto',
          confidence: zoneId ? 'high' : 'low',
          updatedAt: new Date().toISOString(),
        });
      }
      return list;
    });
  };

  const handleImportFlightSchedule = async () => {
    if (!selectedTemplateId) {
      message.error('Please select a template');
      return;
    }
    if (!flightImportFile) {
      message.error('Please choose a flight schedule file');
      return;
    }

    setFlightImporting(true);
    try {
      const response = await importFlightScheduleForTemplate({
        templateId: selectedTemplateId,
        file: flightImportFile,
        timezone: flightImportTimezone || selectedTemplate?.timezone,
      });

      if (!response.success) {
        message.error(response.error || 'Flight import failed');
        return;
      }

      setFlightImportSummary(response.data?.summary || null);
      setFlightImportFile(null);
      message.success(`Imported ${response.data?.summary?.validRows || 0} flight rows`);
      await refreshFlightConfig(selectedTemplateId);
      await loadReport(selectedTemplateId, selectedDate, selectedAssetFilter);
    } catch (error) {
      message.error(`Flight import failed: ${error.message}`);
    } finally {
      setFlightImporting(false);
    }
  };

  const handleSaveFlightMappings = async () => {
    if (!selectedTemplateId) return;

    setSavingFlightMappings(true);
    try {
      const payload = allImportedBayKeys.map((bayKey) => {
        const manualZoneId = flightManualMapByKey[bayKey]?.zoneId || null;
        return {
          bayKey,
          zoneId: manualZoneId,
          method: manualZoneId ? 'manual' : 'auto',
          confidence: manualZoneId ? 'high' : 'low',
        };
      });

      const response = await upsertFlightBayMappings({
        templateId: selectedTemplateId,
        mappings: payload,
      });

      if (!response.success) {
        message.error(response.error || 'Failed to save bay mappings');
        return;
      }

      message.success('Bay mappings saved');
      await refreshFlightConfig(selectedTemplateId);
      await loadReport(selectedTemplateId, selectedDate, selectedAssetFilter);
    } finally {
      setSavingFlightMappings(false);
    }
  };

  const parseCoordinatesText = (text) => {
    const lines = (text || '').split('\n').map((line) => line.trim()).filter(Boolean);
    const coordinates = lines
      .map((line) => line.split(',').map((v) => Number(v.trim())))
      .filter((parts) => parts.length === 2 && Number.isFinite(parts[0]) && Number.isFinite(parts[1]))
      .map(([lat, lng]) => ({ lat, lng }));

    return coordinates;
  };

  const sanitizeZoneName = (name, fallbackIndex) => {
    const trimmed = `${name || ''}`.trim();
    if (trimmed) return trimmed;
    return `Imported Zone ${fallbackIndex + 1}`;
  };

  const convertGeoRingToLatLng = (ring) => {
    if (!Array.isArray(ring)) return [];
    return ring
      .map((pair) => {
        if (!Array.isArray(pair) || pair.length < 2) return null;
        const lng = Number(pair[0]);
        const lat = Number(pair[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
      })
      .filter(Boolean);
  };

  const extractZonesFromGeoJson = (geoJson) => {
    if (!geoJson || !Array.isArray(geoJson.features)) {
      throw new Error('Invalid GeoJSON: missing features array');
    }

    const zones = [];
    geoJson.features.forEach((feature, featureIndex) => {
      const geomType = feature?.geometry?.type;
      const coordinates = feature?.geometry?.coordinates;
      const baseName = sanitizeZoneName(feature?.properties?.name, featureIndex);

      if (geomType === 'Polygon' && Array.isArray(coordinates) && coordinates[0]) {
        const outerRing = convertGeoRingToLatLng(coordinates[0]);
        if (outerRing.length >= 3) {
          zones.push({
            name: baseName,
            coordinates: outerRing,
          });
        }
        return;
      }

      if (geomType === 'MultiPolygon' && Array.isArray(coordinates)) {
        coordinates.forEach((polygon, polygonIndex) => {
          if (!Array.isArray(polygon) || !polygon[0]) return;
          const outerRing = convertGeoRingToLatLng(polygon[0]);
          if (outerRing.length >= 3) {
            zones.push({
              name: `${baseName} ${polygonIndex + 1}`,
              coordinates: outerRing,
            });
          }
        });
      }
    });

    return zones;
  };

  const fetchGeoJsonWithFallback = async (url) => {
    const attemptParse = async (response) => {
      if (!response.ok) {
        throw new Error(`Request failed with ${response.status}`);
      }
      return response.json();
    };

    try {
      const direct = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json, application/geo+json, application/octet-stream, */*',
        },
      });
      return await attemptParse(direct);
    } catch (directError) {
      // Fallback for local dev where browser CORS blocks cross-domain GeoJSON
      const proxy = await fetch(`/geojson-proxy?url=${encodeURIComponent(url)}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });
      return await attemptParse(proxy);
    }
  };

  const readGeoJsonFile = (file) => {
    if (!file) {
      throw new Error('GeoJSON file is required');
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result);
          resolve(parsed);
        } catch (error) {
          reject(new Error('Invalid JSON file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const addZone = () => {
    if (!templateDraft) return;

    const coordinates = parseCoordinatesText(newZone.coordinatesText);
    if (!newZone.name.trim()) {
      message.error('Zone name is required');
      return;
    }
    if (coordinates.length < 3) {
      message.error('Please enter at least 3 polygon points');
      return;
    }

    const zoneId = `zone_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const zone = {
      id: zoneId,
      name: newZone.name.trim(),
      zoneType: 'polygon',
      isBase: newZone.isBase,
      coordinates,
      metadata: {},
    };

    const next = {
      ...templateDraft,
      zones: [...templateDraft.zones, zone],
    };
    setTemplateDraft(next);
    setNewZone({ name: '', isBase: false, coordinatesText: '' });
    setZoneModalVisible(false);
  };

  const importGeoJsonZones = async () => {
    if (!templateDraft) return;
    const importUrl = geoImportUrl.trim();
    if (!geoImportFile && !importUrl) {
      message.error('GeoJSON URL or file is required');
      return;
    }

    setGeoImportLoading(true);
    try {
      const data = geoImportFile
        ? await readGeoJsonFile(geoImportFile)
        : await fetchGeoJsonWithFallback(importUrl);
      const imported = extractZonesFromGeoJson(data);
      if (imported.length === 0) {
        throw new Error('No valid Polygon/MultiPolygon features found');
      }

      const existingNames = new Set((templateDraft.zones || []).map((z) => z.name.toLowerCase()));
      const generated = imported.map((zone, idx) => {
        let finalName = zone.name;
        let suffix = 1;
        while (existingNames.has(finalName.toLowerCase())) {
          suffix += 1;
          finalName = `${zone.name} (${suffix})`;
        }
        existingNames.add(finalName.toLowerCase());

        return {
          id: `zone_import_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 6)}`,
          name: finalName,
          zoneType: 'polygon',
          isBase: geoImportAsBase && idx === 0,
          coordinates: zone.coordinates,
          metadata: { importedFrom: geoImportFile ? (geoImportFile.name || 'local-file') : importUrl },
        };
      });

      setTemplateDraft({
        ...templateDraft,
        zones: [...(templateDraft.zones || []), ...generated],
      });
      setGeoImportModalVisible(false);
      setGeoImportFile(null);
      message.success(`Imported ${generated.length} geofence zone(s)`);
    } catch (error) {
      message.error(`GeoJSON import failed: ${error.message}`);
    } finally {
      setGeoImportLoading(false);
    }
  };

  const removeZone = (zoneId) => {
    if (!templateDraft) return;
    const nextMetrics = (templateDraft.metrics || []).filter((m) => {
      const cfg = m.config || {};
      return cfg.zoneId !== zoneId && cfg.sourceZoneId !== zoneId && cfg.destinationZoneId !== zoneId && cfg.baseZoneId !== zoneId;
    });

    setTemplateDraft({
      ...templateDraft,
      zones: templateDraft.zones.filter((z) => z.id !== zoneId),
      metrics: nextMetrics,
    });
  };

  const clearImportedGeoJsonZones = () => {
    if (!templateDraft) return;
    const zones = templateDraft.zones || [];
    const importedZoneIds = new Set(
      zones
        .filter((zone) => {
          const importedFromMetadata = zone?.metadata && Object.prototype.hasOwnProperty.call(zone.metadata, 'importedFrom');
          const importedFromLegacyId = `${zone?.id || ''}`.startsWith('zone_import_');
          return importedFromMetadata || importedFromLegacyId;
        })
        .map((zone) => zone.id)
    );

    if (importedZoneIds.size === 0) {
      message.info('No imported GeoJSON zones found');
      return;
    }

    const nextZones = zones.filter((zone) => !importedZoneIds.has(zone.id));
    const nextMetrics = (templateDraft.metrics || []).filter((metric) => {
      const cfg = metric.config || {};
      return !importedZoneIds.has(cfg.zoneId)
        && !importedZoneIds.has(cfg.sourceZoneId)
        && !importedZoneIds.has(cfg.destinationZoneId)
        && !importedZoneIds.has(cfg.baseZoneId);
    });

    setTemplateDraft({
      ...templateDraft,
      zones: nextZones,
      metrics: nextMetrics,
    });

    if (zoneMapPreviewZoneId !== 'all' && importedZoneIds.has(zoneMapPreviewZoneId)) {
      setZoneMapPreviewZoneId('all');
    }

    message.success(`Cleared ${importedZoneIds.size} imported GeoJSON zone(s)`);
  };

  const addMetric = () => {
    if (!templateDraft) return;
    if (!newMetric.label.trim()) {
      message.error('Metric label is required');
      return;
    }

    const metricType = newMetric.type;
    const config = {};

    if (metricType === 'trip_count' || metricType === 'round_trip_count') {
      if (!newMetric.sourceZoneId || !newMetric.destinationZoneId) {
        message.error('Source and destination zones are required');
        return;
      }
      config.sourceZoneId = newMetric.sourceZoneId;
      config.destinationZoneId = newMetric.destinationZoneId;
    }

    if (['zone_entries', 'zone_exits', 'reentry_count', 'time_inside'].includes(metricType)) {
      if (!newMetric.zoneId) {
        message.error('Zone is required for this metric');
        return;
      }
      config.zoneId = newMetric.zoneId;
    }

    if (['time_outside_base', 'base_to_multi_poi_visits'].includes(metricType)) {
      if (!newMetric.baseZoneId) {
        message.error('Base zone is required for this metric');
        return;
      }
      config.baseZoneId = newMetric.baseZoneId;
    }

    if (metricType === 'unique_poi_count') {
      config.excludeBase = true;
    }

    const metric = {
      id: `metric_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      type: metricType,
      label: newMetric.label.trim(),
      config,
    };

    setTemplateDraft({
      ...templateDraft,
      metrics: [...templateDraft.metrics, metric],
    });

    setNewMetric({
      label: '',
      type: 'zone_entries',
      sourceZoneId: undefined,
      destinationZoneId: undefined,
      zoneId: undefined,
      baseZoneId: undefined,
    });
    setMetricModalVisible(false);
  };

  const removeMetric = (metricId) => {
    if (!templateDraft) return;
    setTemplateDraft({
      ...templateDraft,
      metrics: templateDraft.metrics.filter((m) => m.id !== metricId),
    });
  };

  const handleSetRangeStart = (record) => {
    if (!record?.id || !Number.isFinite(Number(record.startTs))) return;
    setSelectedMapEvent(null);
    setRangeStartEventId(record.id);
    if (rangeEndEventId) {
      const endEvent = playbackSelectableEvents.find((e) => e.id === rangeEndEventId);
      if (!endEvent || Number(endEvent.startTs) < Number(record.startTs)) {
        setRangeEndEventId(null);
      }
    }
  };

  const handleSetRangeEnd = (record) => {
    if (!record?.id || !Number.isFinite(Number(record.startTs))) return;
    if (!rangeStartEventId) {
      message.warning('Set a Start event first');
      return;
    }
    const startEvent = playbackSelectableEvents.find((e) => e.id === rangeStartEventId);
    if (startEvent && Number(record.startTs) < Number(startEvent.startTs)) {
      message.warning('End event must be after Start event');
      return;
    }
    setSelectedMapEvent(null);
    setRangeEndEventId(record.id);
  };

  const openEvents = async (record) => {
    if (!selectedTemplateId || !record?.assetId) return;

    setEventsDrawerVisible(true);
    setEventsLoading(true);
    setTrackLoading(true);
    setEventsAsset(record);
    setEventChannelFilter('all');
    setEventTypeFilter([]);
    setSelectedMapEvent(null);
    setRangeStartEventId(null);
    setRangeEndEventId(null);
    setIsMapPlaying(false);
    try {
      const dateKey = moment(selectedDate).format('YYYY-MM-DD');
      const [eventsResponse, trackResponse] = await Promise.all([
        fetchUtilizationEvents({
          templateId: selectedTemplateId,
          assetId: record.assetId,
          date: dateKey,
        }),
        fetchUtilizationTrack({
          templateId: selectedTemplateId,
          assetId: record.assetId,
          date: dateKey,
        }),
      ]);

      if (eventsResponse.success) {
        const nextEvents = eventsResponse.data || [];
        setEventsData(nextEvents);
        const firstZoneEvent = nextEvents.find((e) => e.zoneId && e.eventType !== 'GAP');
        if (firstZoneEvent) setSelectedMapEvent(firstZoneEvent);
      } else {
        message.error(eventsResponse.error || 'Failed to load events');
      }

      if (trackResponse.success) {
        setTrackPoints(trackResponse.data || []);
      } else {
        setTrackPoints([]);
        message.warning(trackResponse.error || 'Track data unavailable for map playback');
      }
    } finally {
      setEventsLoading(false);
      setTrackLoading(false);
    }
  };

  const exportSummaryCsv = () => {
    if (!report || !selectedTemplate) return;

    const headers = ['Date', 'Asset Code', ...(selectedTemplate.metrics || []).map((m) => m.label)];
    const rows = (report.rows || []).map((row) => {
      return [
        row.date,
        row.assetCode,
        ...(selectedTemplate.metrics || []).map((metric) => {
          const value = row[metric.id] ?? row[metric.label] ?? row[metric.type];
          const unit = metricUnit(metric.type, report.comparison?.[metric.id]?.unit);
          return formatMetricValue(value, unit);
        }),
      ];
    });

    downloadCSV(`utilization_summary_${moment(selectedDate).format('YYYYMMDD')}.csv`, headers, rows);
  };

  const exportEventsCsv = () => {
    if (!filteredEventsData || filteredEventsData.length === 0) return;

    const headers = [
      'Asset',
      'Event Type',
      'Detection Mode',
      'Distance (m)',
      'Zone',
      'Zone ID',
      'Start Time',
      'End Time',
      'Duration (sec)',
      'Payload',
    ];
    const rows = filteredEventsData.map((event) => [
      event.assetCode,
      event.eventType,
      event.payloadJson?.detectionMode || (event.eventType?.startsWith('SERVICE_') ? 'proximity' : 'polygon'),
      event.payloadJson?.distanceMeters ?? '-',
      event.zoneName || event.zoneId || '-',
      event.zoneId || '-',
      moment(event.startTs).format('YYYY-MM-DD HH:mm:ss'),
      event.endTs ? moment(event.endTs).format('YYYY-MM-DD HH:mm:ss') : '-',
      event.durationSec || '-',
      JSON.stringify(event.payloadJson || {}),
    ]);

    downloadCSV(`utilization_events_${eventsAsset?.assetCode || 'asset'}_${moment(selectedDate).format('YYYYMMDD')}.csv`, headers, rows);
  };

  const exportFlightContextByFlightCsv = () => {
    if (!flightContext?.flights || flightContext.flights.length === 0) return;

    const headers = [
      'Date',
      'Airport',
      'ARR Flight',
      'DEP Flight',
      'ATA',
      'ATD',
      'Bay(s)',
      'Mapped Zone(s)',
      'Matched Equipment Count',
      'Matched Equipment',
    ];

    const rows = flightContext.flights.map((flight) => [
      flight.localDate,
      flight.airportName,
      flight.arrFlightNo,
      flight.depFlightNo,
      flight.ataLocal,
      flight.atdLocal,
      (flight.bayKeys || []).join('|'),
      (flight.mappedZoneNames || []).join('|'),
      flight.matchedEquipmentCount || 0,
      (flight.matches || []).map((m) => `${m.assetCode}(${m.matchChannel})`).join('|'),
    ]);

    downloadCSV(
      `flight_context_by_flight_${moment(selectedDate).format('YYYYMMDD')}.csv`,
      headers,
      rows
    );
  };

  const exportFlightContextByEquipmentCsv = () => {
    if (!flightContext?.equipment || flightContext.equipment.length === 0) return;

    const headers = [
      'Asset Code',
      'Flights Handled',
      'Polygon Flights',
      'Service-Only Flights',
      'Matched Flights',
    ];

    const rows = flightContext.equipment.map((item) => [
      item.assetCode,
      item.flightsHandled || 0,
      item.polygonFlights || 0,
      item.serviceOnlyFlights || 0,
      (item.matchedFlights || [])
        .map((f) => `${f.arrFlightNo || '-'}->${f.depFlightNo || '-'}(${f.matchChannel})`)
        .join('|'),
    ]);

    downloadCSV(
      `flight_context_by_equipment_${moment(selectedDate).format('YYYYMMDD')}.csv`,
      headers,
      rows
    );
  };

  const filteredEventsData = useMemo(() => {
    const all = timelineEventsData;
    const afterTypeFilter = Array.isArray(eventTypeFilter) && eventTypeFilter.length > 0
      ? all.filter((e) => eventTypeFilter.includes(e.eventType))
      : all;

    if (eventChannelFilter === 'polygon') {
      return afterTypeFilter.filter((e) => ['ENTER', 'EXIT', 'DWELL', 'TRIP'].includes(e.eventType));
    }
    if (eventChannelFilter === 'service') {
      return afterTypeFilter.filter((e) => ['SERVICE_ENTER', 'SERVICE_EXIT', 'SERVICE_DWELL'].includes(e.eventType));
    }
    if (eventChannelFilter === 'gaps') {
      return afterTypeFilter.filter((e) => e.eventType === 'GAP');
    }
    return afterTypeFilter;
  }, [eventChannelFilter, timelineEventsData, eventTypeFilter]);

  const availableEventTypes = useMemo(() => {
    const unique = Array.from(new Set((timelineEventsData || []).map((e) => e.eventType).filter(Boolean)));
    return unique.sort((a, b) => a.localeCompare(b));
  }, [timelineEventsData]);

  const zoneVisitMatrix = useMemo(() => {
    const thresholdSec = Number(selectedTemplate?.rules?.dwellThresholdSec || 120);
    const zoneMap = {};
    (selectedTemplate?.zones || []).forEach((zone) => {
      zoneMap[zone.id] = zone.name;
    });

    const ordered = [...(eventsData || [])].sort((a, b) => (a.startTs || 0) - (b.startTs || 0));

    const countBy = (enterType, exitType) => {
      const activeEnterByZone = {};
      const rows = [];
      ordered.forEach((event) => {
        if (!event || !event.zoneId) return;
        if (event.eventType === enterType) {
          activeEnterByZone[event.zoneId] = event.startTs;
          return;
        }
        if (event.eventType === exitType) {
          const enterTs = activeEnterByZone[event.zoneId];
          if (!enterTs) return;
          const durationSec = Math.max(0, Math.round((event.startTs - enterTs) / 1000));
          if (durationSec >= thresholdSec) {
            rows.push({
              key: `visit_${enterType}_${event.zoneId}_${enterTs}_${event.startTs}`,
              zoneId: event.zoneId,
              zoneName: event.zoneName || zoneMap[event.zoneId] || event.zoneId,
              enterTs,
              exitTs: event.startTs,
              durationSec,
            });
          }
          delete activeEnterByZone[event.zoneId];
        }
      });

      return {
        enterCount: ordered.filter((e) => e.eventType === enterType).length,
        exitCount: ordered.filter((e) => e.eventType === exitType).length,
        rows,
      };
    };

    const polygon = countBy('ENTER', 'EXIT');
    const service = countBy('SERVICE_ENTER', 'SERVICE_EXIT');
    const active = matrixMode === 'service' ? service : polygon;

    return {
      thresholdSec,
      rows: active.rows,
      totals: {
        polygonEntries: polygon.enterCount,
        polygonExits: polygon.exitCount,
        polygonValidVisits: polygon.rows.length,
        serviceEntries: service.enterCount,
        serviceExits: service.exitCount,
        serviceValidVisits: service.rows.length,
        zonesEntered: new Set(active.rows.map((r) => r.zoneId)).size,
        validVisits: active.rows.length,
      },
    };
  }, [eventsData, selectedTemplate, matrixMode]);

  const activePlaybackPoint = useMemo(() => {
    if (!mapPlaybackPoints || mapPlaybackPoints.length === 0) return null;
    const idx = Math.max(0, Math.min(playbackIndex, mapPlaybackPoints.length - 1));
    return mapPlaybackPoints[idx];
  }, [mapPlaybackPoints, playbackIndex]);

  const playbackState = useMemo(() => resolveMotionState(activePlaybackPoint), [activePlaybackPoint]);

  const playbackIconByState = useMemo(() => {
    const makeIcon = (state, color, symbol) => L.divIcon({
      className: `playback-pin-icon playback-pin-${state.toLowerCase()}`,
      html: `
        <div style="position:relative;width:22px;height:22px;">
          <div style="position:absolute;left:1px;top:1px;width:20px;height:20px;background:${color};border:2px solid #ffffff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 1px 4px rgba(0,0,0,0.35);"></div>
          <div style="position:absolute;left:7px;top:3px;width:10px;height:10px;color:#ffffff;font-weight:700;font-size:10px;line-height:10px;text-align:center;">${symbol}</div>
        </div>
      `,
      iconSize: [22, 22],
      iconAnchor: [11, 22],
    });

    return {
      PLAY: makeIcon('PLAY', '#52c41a', '&#9658;'),
      PAUSE: makeIcon('PAUSE', '#faad14', '&#10074;&#10074;'),
      STOP: makeIcon('STOP', '#ff4d4f', '&#9632;'),
    };
  }, []);

  const playbackPinIcon = playbackIconByState[playbackState] || playbackIconByState.STOP;

  const activePlaybackTimeLabel = useMemo(
    () => (activePlaybackPoint ? moment(activePlaybackPoint.ts).format('YYYY-MM-DD HH:mm:ss') : ''),
    [activePlaybackPoint]
  );

  const eventStateMixById = useMemo(() => {
    const result = {};
    const points = Array.isArray(trackPoints) ? trackPoints : [];
    if (points.length === 0) return result;

    const sortedEvents = (Array.isArray(filteredEventsData) ? [...filteredEventsData] : [])
      .filter((e) => Number.isFinite(Number(e.startTs)))
      .sort((a, b) => Number(a.startTs) - Number(b.startTs));

    const nextStartById = {};
    for (let i = 0; i < sortedEvents.length - 1; i += 1) {
      nextStartById[sortedEvents[i].id] = Number(sortedEvents[i + 1].startTs);
    }

    const lowerBound = (ts) => {
      let lo = 0;
      let hi = points.length;
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (Number(points[mid].ts) < ts) lo = mid + 1;
        else hi = mid;
      }
      return lo;
    };

    sortedEvents.forEach((event) => {
      const start = Number(event.startTs);
      const explicitEnd = Number(event.endTs);
      let end = Number.isFinite(explicitEnd) ? explicitEnd : nextStartById[event.id];
      if (!Number.isFinite(end)) end = start + 5 * 60 * 1000;
      if (!Number.isFinite(start) || end <= start) {
        result[event.id] = { playPct: 0, pausePct: 0, stopPct: 0 };
        return;
      }

      let idx = Math.max(0, lowerBound(start) - 1);
      let playMs = 0;
      let pauseMs = 0;
      let stopMs = 0;

      while (idx < points.length) {
        const p = points[idx];
        const pTs = Number(p.ts);
        if (!Number.isFinite(pTs) || pTs > end) break;

        const nextTsRaw = idx + 1 < points.length ? Number(points[idx + 1].ts) : end;
        const nextTs = Number.isFinite(nextTsRaw) ? nextTsRaw : end;
        const segStart = Math.max(start, pTs);
        const segEnd = Math.min(end, nextTs);
        const segMs = Math.max(0, segEnd - segStart);

        if (segMs > 0) {
          const state = resolveMotionState(p);
          if (state === 'PLAY') playMs += segMs;
          else if (state === 'PAUSE') pauseMs += segMs;
          else stopMs += segMs;
        }

        if (nextTs >= end) break;
        idx += 1;
      }

      const total = playMs + pauseMs + stopMs;
      if (total <= 0) {
        result[event.id] = { playPct: 0, pausePct: 0, stopPct: 0 };
        return;
      }

      result[event.id] = {
        playPct: (playMs * 100) / total,
        pausePct: (pauseMs * 100) / total,
        stopPct: (stopMs * 100) / total,
      };
    });

    return result;
  }, [filteredEventsData, trackPoints]);

  const playbackTimelineEvents = useMemo(() => {
    let items = [...filteredEventsData]
      .filter((e) => e && Number.isFinite(Number(e.startTs)))
      .sort((a, b) => Number(a.startTs) - Number(b.startTs));

    if (selectedRangeEvents) {
      items = items.filter((e) => {
        const start = Number(e.startTs || 0);
        const end = Number(e.endTs || e.startTs || 0);
        return end >= selectedRangeEvents.startTs && start <= selectedRangeEvents.endTs;
      });
    }

    return items;
  }, [filteredEventsData, selectedRangeEvents]);

  const currentAnimatedEventId = useMemo(() => {
    if (!activePlaybackPoint || playbackTimelineEvents.length === 0) return null;
    const ts = Number(activePlaybackPoint.ts || 0);
    if (!Number.isFinite(ts)) return null;

    let fallbackId = null;
    for (let i = 0; i < playbackTimelineEvents.length; i += 1) {
      const ev = playbackTimelineEvents[i];
      const start = Number(ev.startTs || 0);
      const explicitEnd = Number(ev.endTs);
      const nextStart = i + 1 < playbackTimelineEvents.length ? Number(playbackTimelineEvents[i + 1].startTs || 0) : null;

      let end = Number.isFinite(explicitEnd) ? explicitEnd : (Number.isFinite(nextStart) ? nextStart : start + 1000);
      if (end < start) end = start;

      if (ts >= start && ts <= end) return ev.id;
      if (ts >= start) fallbackId = ev.id;
    }

    return fallbackId;
  }, [activePlaybackPoint, playbackTimelineEvents]);

  const matchedFlightsForDrawerAsset = useMemo(() => {
    if (!flightContext?.equipment || !eventsAsset?.assetId) return [];
    const found = flightContext.equipment.find((item) => item.assetId === eventsAsset.assetId);
    return found?.matchedFlights || [];
  }, [flightContext, eventsAsset]);

  const exportZoneMatrixCsv = () => {
    if (!zoneVisitMatrix.rows || zoneVisitMatrix.rows.length === 0) return;

    const headers = ['Matrix Mode', 'Zone', 'Entry Time', 'Exit Time', 'Time Spent (sec)', 'Time Spent (human)'];
    const rows = zoneVisitMatrix.rows.map((row) => [
      matrixMode === 'service' ? 'service' : 'polygon',
      row.zoneName,
      moment(row.enterTs).format('YYYY-MM-DD HH:mm:ss'),
      moment(row.exitTs).format('YYYY-MM-DD HH:mm:ss'),
      row.durationSec,
      formatDuration(row.durationSec),
    ]);

    downloadCSV(
      `zone_visit_matrix_${eventsAsset?.assetCode || 'asset'}_${moment(selectedDate).format('YYYYMMDD')}.csv`,
      headers,
      rows
    );
  };

  const tableColumns = useMemo(() => {
    const metricColumns = (selectedTemplate?.metrics || []).map((metric) => ({
      title: metric.label,
      dataIndex: metric.id,
      key: metric.id,
      render: (value) => {
        const unit = metricUnit(metric.type, report?.comparison?.[metric.id]?.unit);
        return formatMetricValue(value, unit);
      },
    }));

    return [
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        width: 120,
      },
      {
        title: 'Asset Code',
        dataIndex: 'assetCode',
        key: 'assetCode',
        width: 140,
      },
      ...metricColumns,
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_, record) => (
          <Button size="small" onClick={() => openEvents(record)}>
            View Events
          </Button>
        ),
      },
    ];
  }, [selectedTemplate, report]);

  const eventColumns = [
    {
      title: 'Start/Stop',
      key: 'rangeActions',
      width: 140,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button
            size="small"
            type={rangeStartEventId === record.id ? 'primary' : 'default'}
            onClick={(e) => {
              e.stopPropagation();
              handleSetRangeStart(record);
            }}
          >
            Start
          </Button>
          <Button
            size="small"
            type={rangeEndEventId === record.id ? 'primary' : 'default'}
            onClick={(e) => {
              e.stopPropagation();
              handleSetRangeEnd(record);
            }}
            disabled={!rangeStartEventId}
          >
            End
          </Button>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'eventType',
      key: 'eventType',
      width: 130,
      render: (value) => {
        const colorMap = {
          ENTER: 'green',
          EXIT: 'orange',
          DWELL: 'blue',
          SERVICE_ENTER: 'cyan',
          SERVICE_EXIT: 'geekblue',
          SERVICE_DWELL: 'blue',
          TRIP: 'purple',
          GAP: 'red',
          OUTSIDE: 'default',
        };
        if (value === 'OUTSIDE') {
          return (
            <Tag style={{ background: '#f0f0f0', color: '#262626', borderColor: '#d9d9d9' }}>
              OUTSIDE
            </Tag>
          );
        }
        return <Tag color={colorMap[value] || 'default'}>{value}</Tag>;
      },
    },
    {
      title: 'Zone',
      key: 'zoneName',
      width: 220,
      render: (_, record) => (
        <div>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto' }}
            disabled={!record.zoneId}
            onClick={() => {
              setRangeStartEventId(null);
              setRangeEndEventId(null);
              setSelectedMapEvent(record);
            }}
          >
            {record.zoneName || record.zoneId || '-'}
          </Button>
        </div>
      ),
    },
    {
      title: 'Detection',
      key: 'detectionMode',
      width: 120,
      render: (_, record) => (
        record.payloadJson?.detectionMode
        || (record.eventType === 'OUTSIDE' ? 'outside' : null)
        || (record.eventType?.startsWith('SERVICE_') ? 'proximity' : (record.eventType === 'GAP' ? '-' : 'polygon'))
      ),
    },
    {
      title: 'Distance (m)',
      key: 'distanceMeters',
      width: 110,
      render: (_, record) => (record.payloadJson?.distanceMeters ?? '-'),
    },
    {
      title: 'Start',
      dataIndex: 'startTs',
      key: 'startTs',
      width: 180,
      render: (value) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'End',
      dataIndex: 'endTs',
      key: 'endTs',
      width: 180,
      render: (value) => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: 'Duration',
      dataIndex: 'durationSec',
      key: 'durationSec',
      width: 110,
      render: (value) => (value ? formatDuration(value) : '-'),
    },
    {
      title: 'Play %',
      key: 'playPct',
      width: 90,
      render: (_, record) => `${Number((eventStateMixById[record.id]?.playPct || 0)).toFixed(1)}%`,
    },
    {
      title: 'Pause %',
      key: 'pausePct',
      width: 90,
      render: (_, record) => `${Number((eventStateMixById[record.id]?.pausePct || 0)).toFixed(1)}%`,
    },
    {
      title: 'Stop %',
      key: 'stopPct',
      width: 90,
      render: (_, record) => `${Number((eventStateMixById[record.id]?.stopPct || 0)).toFixed(1)}%`,
    },
    {
      title: 'P',
      dataIndex: 'payloadJson',
      key: 'payloadJson',
      render: (value) => (
        <Popover
          trigger="click"
          placement="left"
          content={(
            <pre
              style={{
                margin: 0,
                fontSize: 11,
                maxWidth: 380,
                maxHeight: 280,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(value || {}, null, 2)}
            </pre>
          )}
        >
          <Button size="small" shape="circle">i</Button>
        </Popover>
      ),
    },
    {
      title: 'Debounced',
      key: 'debounceApplied',
      width: 95,
      render: (_, record) => renderTickCross(record.payloadJson?.debounceApplied),
    },
    {
      title: 'Uncertain Gap',
      key: 'uncertainGapBefore',
      width: 105,
      render: (_, record) => renderTickCross(record.payloadJson?.uncertainGapBefore),
    },
  ];

  if (loadingInit) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>Loading Asset Utilization Studio...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 8 }}>
      <Row gutter={16}>
        <Col span={24}>
          <Card>
            <Row gutter={16} type="flex" align="middle">
              <Col span={6}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Template</div>
                <Select
                  style={{ width: '100%' }}
                  value={selectedTemplateId}
                  onChange={(value) => setSelectedTemplateId(value)}
                >
                  {templates.map((template) => (
                    <Option key={template.id} value={template.id}>
                      {template.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={4}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Date</div>
                <DatePicker
                  style={{ width: '100%' }}
                  value={selectedDate}
                  onChange={(date) => setSelectedDate(date || moment())}
                />
              </Col>
              <Col span={8}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Asset Filter</div>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const label = `${option.props.children || ''}`.toLowerCase();
                    return label.includes((input || '').toLowerCase());
                  }}
                  style={{ width: '100%' }}
                  placeholder="All assets"
                  value={selectedAssetFilter}
                  onChange={(vals) => setSelectedAssetFilter(vals)}
                >
                  {assets.map((asset) => (
                    <Option key={asset._id} value={asset._id}>
                      {asset.assetCode}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <div style={{ display: 'flex', gap: 8, marginTop: 28 }}>
                  <Button type="primary" icon="play-circle" loading={computing} onClick={handleCompute}>
                    Compute Daily Utilization
                  </Button>
                  <Button icon="search" loading={loadingReport} onClick={() => loadReport()}>
                    Load Report
                  </Button>
                </div>
              </Col>
            </Row>

            <Divider style={{ margin: '16px 0' }} />

            <Alert
              showIcon
              type="info"
              message="Nightly-style compute"
              description="This screen computes utilization for the selected local day using polygon geofences, debounce transitions, configurable dwell thresholds, and trip rules. For faster compute, select specific assets before running."
            />
          </Card>
        </Col>
      </Row>

      {templateDraft && (
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card
              title="Template Configuration (Ops/Admin)"
              extra={
                <Button type="primary" icon="save" onClick={handleSaveTemplate}>
                  Save Template
                </Button>
              }
            >
              <Row gutter={16}>
                <Col span={8}>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Template Name</div>
                  <Input
                    value={templateDraft.name}
                    onChange={(e) => setTemplateDraft({ ...templateDraft, name: e.target.value })}
                  />
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Timezone</div>
                  <Select
                    showSearch
                    style={{ width: '100%' }}
                    value={templateDraft.timezone}
                    onChange={(value) => setTemplateDraft({ ...templateDraft, timezone: value })}
                    filterOption={(input, option) => option.props.children.toLowerCase().includes(input.toLowerCase())}
                  >
                    {availableTimezones.map((tz) => (
                      <Option key={tz} value={tz}>{tz}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={3}>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Dwell (sec)</div>
                  <InputNumber
                    min={30}
                    style={{ width: '100%' }}
                    value={templateDraft.rules.dwellThresholdSec}
                    onChange={(v) => setTemplateDraft({
                      ...templateDraft,
                      rules: { ...templateDraft.rules, dwellThresholdSec: Number(v || 120) },
                    })}
                  />
                </Col>
                <Col span={3}>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Debounce (pts)</div>
                  <InputNumber
                    min={1}
                    style={{ width: '100%' }}
                    value={templateDraft.rules.debouncePoints}
                    onChange={(v) => setTemplateDraft({
                      ...templateDraft,
                      rules: { ...templateDraft.rules, debouncePoints: Number(v || 2) },
                    })}
                  />
                </Col>
                <Col span={4}>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Trip Max (min)</div>
                  <InputNumber
                    min={10}
                    style={{ width: '100%' }}
                    value={Math.round((templateDraft.rules.tripMaxDurationSec || 0) / 60)}
                    onChange={(v) => setTemplateDraft({
                      ...templateDraft,
                      rules: { ...templateDraft.rules, tripMaxDurationSec: Number(v || 480) * 60 },
                    })}
                  />
                </Col>
              </Row>

              <Row gutter={16} style={{ marginTop: 12 }}>
                <Col span={6}>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Proximity Enabled</div>
                  <Switch
                    checked={!!templateDraft.rules.proximityEnabled}
                    onChange={(checked) => setTemplateDraft({
                      ...templateDraft,
                      rules: { ...templateDraft.rules, proximityEnabled: checked },
                    })}
                  />
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Default Cutoff (m)</div>
                  <InputNumber
                    min={1}
                    style={{ width: '100%' }}
                    value={templateDraft.rules.defaultProximityCutoffMeters}
                    onChange={(v) => setTemplateDraft({
                      ...templateDraft,
                      rules: { ...templateDraft.rules, defaultProximityCutoffMeters: Number(v || 30) },
                    })}
                  />
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Exit Hysteresis (%)</div>
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    value={templateDraft.rules.proximityExitHysteresisPct}
                    onChange={(v) => setTemplateDraft({
                      ...templateDraft,
                      rules: { ...templateDraft.rules, proximityExitHysteresisPct: Number(v || 20) },
                    })}
                  />
                </Col>
              </Row>

              <Divider />

              <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                <Title level={5} style={{ margin: 0 }}>Per Asset-Type Proximity Cutoffs</Title>
                <Text type="secondary">Subtype/type key from selected date assets</Text>
              </div>

              <Table
                size="small"
                pagination={false}
                dataSource={assetTypeOptions.map((opt) => {
                  const cutoff = templateDraft.rules.proximityByAssetType?.[opt.key];
                  return {
                    key: opt.key,
                    assetTypeKey: opt.key,
                    assetTypeLabel: opt.label,
                    enabled: Number.isFinite(Number(cutoff)) && Number(cutoff) > 0,
                    cutoffMeters: cutoff,
                  };
                })}
                locale={{ emptyText: 'No asset types found in selected date data.' }}
                columns={[
                  { title: 'Asset Type', dataIndex: 'assetTypeLabel', key: 'assetTypeLabel' },
                  {
                    title: 'Enabled',
                    dataIndex: 'enabled',
                    key: 'enabled',
                    width: 100,
                    render: (enabled, row) => (
                      <Switch
                        size="small"
                        checked={enabled}
                        onChange={(checked) => {
                          const current = { ...(templateDraft.rules.proximityByAssetType || {}) };
                          if (!checked) {
                            delete current[row.assetTypeKey];
                          } else if (!current[row.assetTypeKey]) {
                            current[row.assetTypeKey] = Number(templateDraft.rules.defaultProximityCutoffMeters || 30);
                          }
                          setTemplateDraft({
                            ...templateDraft,
                            rules: { ...templateDraft.rules, proximityByAssetType: current },
                          });
                        }}
                      />
                    ),
                  },
                  {
                    title: 'Cutoff (m)',
                    dataIndex: 'cutoffMeters',
                    key: 'cutoffMeters',
                    width: 140,
                    render: (value, row) => (
                      <InputNumber
                        min={1}
                        style={{ width: '100%' }}
                        value={value}
                        disabled={!row.enabled}
                        onChange={(v) => {
                          const current = { ...(templateDraft.rules.proximityByAssetType || {}) };
                          current[row.assetTypeKey] = Number(v || templateDraft.rules.defaultProximityCutoffMeters || 30);
                          setTemplateDraft({
                            ...templateDraft,
                            rules: { ...templateDraft.rules, proximityByAssetType: current },
                          });
                        }}
                      />
                    ),
                  },
                ]}
              />

              <Divider />

              <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={5} style={{ margin: 0 }}>Flight Context (Aviation)</Title>
                <Text type="secondary">Template-scoped import + bay mapping</Text>
              </div>

              <Row gutter={12} style={{ marginBottom: 8 }}>
                <Col span={6}>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Import Timezone</div>
                  <Select
                    showSearch
                    style={{ width: '100%' }}
                    value={flightImportTimezone || templateDraft.timezone}
                    onChange={(value) => setFlightImportTimezone(value)}
                    filterOption={(input, option) => option.props.children.toLowerCase().includes(input.toLowerCase())}
                  >
                    {availableTimezones.map((tz) => (
                      <Option key={tz} value={tz}>{tz}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={10}>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Flight Schedule File</div>
                  <Upload
                    accept=".xlsx,.xls,.csv"
                    beforeUpload={(file) => {
                      setFlightImportFile(file);
                      return false;
                    }}
                    onRemove={() => setFlightImportFile(null)}
                    fileList={flightImportFile ? [{
                      uid: flightImportFile.uid || 'flight-upload',
                      name: flightImportFile.name || 'flight-schedule',
                      status: 'done',
                    }] : []}
                  >
                    <Button icon="upload">Choose File</Button>
                  </Upload>
                </Col>
                <Col span={8}>
                  <div style={{ marginBottom: 6, fontWeight: 600 }}>Actions</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button type="primary" loading={flightImporting} onClick={handleImportFlightSchedule}>
                      Import Flights
                    </Button>
                    <Button loading={savingFlightMappings} onClick={handleSaveFlightMappings}>
                      Save Bay Mapping
                    </Button>
                  </div>
                </Col>
              </Row>

              {flightImportSummary && (
                <Alert
                  style={{ marginBottom: 10 }}
                  type="success"
                  showIcon
                  message={`Imported ${flightImportSummary.validRows || 0}/${flightImportSummary.rowsParsed || 0} rows`}
                  description={`Date range: ${flightImportSummary.dateFrom || '-'} to ${flightImportSummary.dateTo || '-'} | Airports: ${(flightImportSummary.airports || []).join(', ') || '-'}`}
                />
              )}

              {flightConfigLoading ? (
                <div style={{ textAlign: 'center', padding: 12 }}><Spin size="small" /></div>
              ) : (
                <>
                  <Row gutter={12} style={{ marginBottom: 8 }}>
                    <Col span={8}><Statistic title="Imported Flights" value={(flightImportsData?.rows || []).length} /></Col>
                    <Col span={8}><Statistic title="Bay Keys" value={allImportedBayKeys.length} /></Col>
                    <Col span={8}><Statistic title="Import Batches" value={(flightImportsData?.batches || []).length} /></Col>
                  </Row>

                  {flightBayMappingRows.filter((row) => row.status !== 'mapped').length > 0 && (
                    <Alert
                      style={{ marginBottom: 10 }}
                      type="warning"
                      showIcon
                      message="Unmapped or ambiguous bay keys found"
                      description={flightBayMappingRows
                        .filter((row) => row.status !== 'mapped')
                        .map((row) => row.bayKey)
                        .join(', ')}
                    />
                  )}

                  <Table
                    size="small"
                    pagination={{ pageSize: 8 }}
                    dataSource={flightBayMappingRows}
                    locale={{ emptyText: 'Import a flight schedule to configure bay mappings.' }}
                    columns={[
                      { title: 'Bay Key', dataIndex: 'bayKey', key: 'bayKey', width: 110 },
                      {
                        title: 'Auto Zone',
                        key: 'autoZoneNames',
                        render: (_, row) => (row.autoZoneNames.length > 0 ? row.autoZoneNames.join(', ') : '-'),
                      },
                      {
                        title: 'Manual Override',
                        key: 'manualZoneId',
                        width: 220,
                        render: (_, row) => (
                          <Select
                            allowClear
                            style={{ width: '100%' }}
                            value={row.manualZoneId || undefined}
                            onChange={(value) => upsertManualBayMappingLocal(row.bayKey, value)}
                          >
                            {(templateDraft?.zones || []).map((zone) => (
                              <Option key={zone.id} value={zone.id}>{zone.name}</Option>
                            ))}
                          </Select>
                        ),
                      },
                      { title: 'Effective Zone', dataIndex: 'effectiveZoneName', key: 'effectiveZoneName' },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                        width: 120,
                        render: (value) => {
                          if (value === 'mapped') return <Tag color="green">mapped</Tag>;
                          if (value === 'ambiguous') return <Tag color="orange">ambiguous</Tag>;
                          return <Tag color="red">unmapped</Tag>;
                        },
                      },
                    ]}
                  />
                </>
              )}

              <Divider />

              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Title level={5} style={{ margin: 0 }}>Geofences (Polygon)</Title>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        size="small"
                        icon="environment"
                        onClick={() => openZoneMapPreview('all')}
                        disabled={!templateDraft.zones || templateDraft.zones.length === 0}
                      >
                        View Map
                      </Button>
                      <Popconfirm
                        title="Clear all imported GeoJSON zones?"
                        onConfirm={clearImportedGeoJsonZones}
                        okText="Clear"
                        okType="danger"
                      >
                        <Button size="small" type="danger" icon="delete">
                          Clear Imported
                        </Button>
                      </Popconfirm>
                      <Button size="small" icon="upload" onClick={() => setGeoImportModalVisible(true)}>Import GeoJSON</Button>
                      <Button size="small" icon="plus" onClick={() => setZoneModalVisible(true)}>Add Zone</Button>
                    </div>
                  </div>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={templateDraft.zones.map((z) => ({ ...z, key: z.id }))}
                    columns={[
                      { title: 'Name', dataIndex: 'name', key: 'name' },
                      { title: 'Type', dataIndex: 'zoneType', key: 'zoneType', width: 90 },
                      { title: 'Base', dataIndex: 'isBase', key: 'isBase', width: 70, render: (v) => (v ? <Tag color="blue">Yes</Tag> : '-') },
                      { title: 'Points', key: 'points', width: 70, render: (_, row) => row.coordinates?.length || 0 },
                      {
                        title: '',
                        key: 'actions',
                        width: 120,
                        render: (_, row) => (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <Button size="small" icon="eye" onClick={() => openZoneMapPreview(row.id)} />
                            <Popconfirm title="Delete zone?" onConfirm={() => removeZone(row.id)}>
                              <Button size="small" icon="delete" />
                            </Popconfirm>
                          </div>
                        ),
                      },
                    ]}
                  />
                </Col>

                <Col span={12}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Title level={5} style={{ margin: 0 }}>Metrics</Title>
                    <Button size="small" icon="plus" onClick={() => setMetricModalVisible(true)}>Add Metric</Button>
                  </div>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={templateDraft.metrics.map((m) => ({ ...m, key: m.id }))}
                    columns={[
                      { title: 'Label', dataIndex: 'label', key: 'label' },
                      { title: 'Type', dataIndex: 'type', key: 'type', width: 170 },
                      {
                        title: 'Config',
                        key: 'config',
                        render: (_, row) => <Text code style={{ fontSize: 11 }}>{JSON.stringify(row.config || {})}</Text>,
                      },
                      {
                        title: '',
                        key: 'actions',
                        width: 60,
                        render: (_, row) => (
                          <Popconfirm title="Delete metric?" onConfirm={() => removeMetric(row.id)}>
                            <Button size="small" icon="delete" />
                          </Popconfirm>
                        ),
                      },
                    ]}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card
            title="Utilization Dashboard"
            extra={
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="small"
                  type={dashboardTab === 'utilization' ? 'primary' : 'default'}
                  onClick={() => setDashboardTab('utilization')}
                >
                  Utilization Metrics
                </Button>
                <Button
                  size="small"
                  type={dashboardTab === 'flight' ? 'primary' : 'default'}
                  onClick={() => setDashboardTab('flight')}
                >
                  Flight Context
                </Button>
                {dashboardTab === 'utilization' ? (
                  <Button icon="download" onClick={exportSummaryCsv} disabled={!report || !report.rows || report.rows.length === 0}>
                    Export Summary CSV
                  </Button>
                ) : (
                  <>
                    <Button icon="download" onClick={exportFlightContextByFlightCsv} disabled={!flightContext || !flightContext.flights || flightContext.flights.length === 0}>
                      Export Flights CSV
                    </Button>
                    <Button icon="download" onClick={exportFlightContextByEquipmentCsv} disabled={!flightContext || !flightContext.equipment || flightContext.equipment.length === 0}>
                      Export Equipment CSV
                    </Button>
                  </>
                )}
              </div>
            }
          >
            {loadingReport ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}><Spin /></div>
            ) : !report ? (
              <Alert type="warning" showIcon message="No report loaded" description="Run compute or click Load Report for the selected template and date." />
            ) : dashboardTab === 'utilization' ? (
              <>
                <Row gutter={12} style={{ marginBottom: 16 }}>
                  {(selectedTemplate?.metrics || []).slice(0, 8).map((metric) => {
                    const cmp = report.comparison?.[metric.id];
                    const unit = metricUnit(metric.type, cmp?.unit);
                    const delta = cmp?.delta;
                    const deltaColor = delta > 0 ? '#52c41a' : delta < 0 ? '#ff4d4f' : '#8c8c8c';

                    return (
                      <Col key={metric.id} span={6} style={{ marginBottom: 12 }}>
                        <Card size="small">
                          <Statistic
                            title={metric.label}
                            value={cmp ? cmp.current : 0}
                            formatter={(v) => formatMetricValue(Number(v || 0), unit)}
                          />
                          <div style={{ marginTop: 6, fontSize: 12, color: deltaColor }}>
                            DoD: {cmp ? formatMetricValue(cmp.delta, unit) : '-'}
                          </div>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>

                <Table
                  columns={tableColumns}
                  dataSource={report.rows || []}
                  rowKey={(row) => row.key}
                  scroll={{ x: 'max-content' }}
                  pagination={{ pageSize: 20 }}
                />
              </>
            ) : (
              <>
                <Row gutter={12} style={{ marginBottom: 12 }}>
                  <Col span={5}><Statistic title="Flights Imported" value={flightContext?.summary?.flightsImported || 0} /></Col>
                  <Col span={5}><Statistic title="Flights Matched" value={flightContext?.summary?.flightsMatched || 0} /></Col>
                  <Col span={5}><Statistic title="Equipment Used" value={flightContext?.summary?.equipmentUsed || 0} /></Col>
                  <Col span={5}><Statistic title="Equipment-Flight Matches" value={flightContext?.summary?.equipmentFlightMatches || 0} /></Col>
                  <Col span={4}><Statistic title="Coverage %" value={Number(flightContext?.summary?.coveragePct || 0).toFixed(1)} /></Col>
                </Row>

                {(flightContext?.unmappedBays || []).length > 0 && (
                  <Alert
                    style={{ marginBottom: 12 }}
                    type="warning"
                    showIcon
                    message="Some bay keys are not mapped to zones"
                    description={(flightContext.unmappedBays || []).map((item) => item.bayKey).join(', ')}
                  />
                )}

                <Tabs defaultActiveKey="flights">
                  <TabPane tab="Flights" key="flights">
                    <Table
                      size="small"
                      dataSource={(flightContext?.flights || []).map((row) => ({ ...row, key: row.id }))}
                      pagination={{ pageSize: 10 }}
                      locale={{ emptyText: 'No flights imported for selected date.' }}
                      expandedRowRender={(row) => (
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={(row.matches || []).map((match) => ({ ...match, key: match.id }))}
                          locale={{ emptyText: 'No equipment matched for this flight.' }}
                          columns={[
                            { title: 'Asset', dataIndex: 'assetCode', key: 'assetCode' },
                            { title: 'Channel', dataIndex: 'matchChannel', key: 'matchChannel', width: 100 },
                            { title: 'Zone', dataIndex: 'matchedZoneName', key: 'matchedZoneName' },
                            { title: 'Overlap (sec)', dataIndex: 'overlapSec', key: 'overlapSec', width: 120 },
                          ]}
                        />
                      )}
                      columns={[
                        { title: 'ATA', dataIndex: 'ataLocal', key: 'ataLocal', width: 170 },
                        { title: 'ATD', dataIndex: 'atdLocal', key: 'atdLocal', width: 170 },
                        { title: 'ARR Flight', dataIndex: 'arrFlightNo', key: 'arrFlightNo', width: 130 },
                        { title: 'DEP Flight', dataIndex: 'depFlightNo', key: 'depFlightNo', width: 130 },
                        { title: 'Bay(s)', key: 'bayKeys', render: (_, row) => (row.bayKeys || []).join(', ') || '-' },
                        { title: 'Mapped Zone(s)', key: 'mappedZoneNames', render: (_, row) => (row.mappedZoneNames || []).join(', ') || '-' },
                        { title: 'Matched Equipment', dataIndex: 'matchedEquipmentCount', key: 'matchedEquipmentCount', width: 140 },
                      ]}
                    />
                  </TabPane>
                  <TabPane tab="Equipment" key="equipment">
                    <Table
                      size="small"
                      dataSource={(flightContext?.equipment || []).map((row) => ({ ...row, key: row.assetId }))}
                      pagination={{ pageSize: 10 }}
                      locale={{ emptyText: 'No equipment matched for selected date.' }}
                      columns={[
                        { title: 'Asset Code', dataIndex: 'assetCode', key: 'assetCode', width: 140 },
                        { title: 'Flights Handled', dataIndex: 'flightsHandled', key: 'flightsHandled', width: 130 },
                        { title: 'Polygon Flights', dataIndex: 'polygonFlights', key: 'polygonFlights', width: 130 },
                        { title: 'Service-Only Flights', dataIndex: 'serviceOnlyFlights', key: 'serviceOnlyFlights', width: 150 },
                        {
                          title: 'Matched Flights',
                          key: 'matchedFlights',
                          render: (_, row) => (row.matchedFlights || [])
                            .map((flight) => `${flight.arrFlightNo || '-'} -> ${flight.depFlightNo || '-'} (${flight.matchChannel})`)
                            .join(' | '),
                        },
                      ]}
                    />
                  </TabPane>
                </Tabs>
              </>
            )}
          </Card>
        </Col>
      </Row>

      <Drawer
        visible={eventsDrawerVisible}
        onClose={() => {
          setEventsDrawerVisible(false);
          setIsMapPlaying(false);
          setSelectedMapEvent(null);
          setRangeStartEventId(null);
          setRangeEndEventId(null);
        }}
        width={isDrawerFullWidth ? '96vw' : 980}
        title={`Event Timeline${eventsAsset ? ` - ${eventsAsset.assetCode}` : ''}`}
      >
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Tag color="blue">{moment(selectedDate).format('DD MMM YYYY')}</Tag>
            {selectedTemplate && <Tag color="purple">{selectedTemplate.name}</Tag>}
            <Tag color="gold">Min Zone Stay: {zoneVisitMatrix.thresholdSec}s</Tag>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type={isDrawerFullWidth ? 'primary' : 'default'}
              icon={isDrawerFullWidth ? 'shrink' : 'arrows-alt'}
              onClick={() => setIsDrawerFullWidth((v) => !v)}
            >
              {isDrawerFullWidth ? 'Collapse Drawer' : 'Expand Drawer'}
            </Button>
            <Button icon="table" onClick={exportZoneMatrixCsv} disabled={zoneVisitMatrix.rows.length === 0}>Export Zone Matrix CSV</Button>
            <Button icon="download" onClick={exportEventsCsv} disabled={filteredEventsData.length === 0}>Export Events CSV</Button>
          </div>
        </div>

        <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Text strong>Matrix:</Text>
          <Button
            size="small"
            type={matrixMode === 'polygon' ? 'primary' : 'default'}
            onClick={() => setMatrixMode('polygon')}
          >
            Polygon Visits
          </Button>
          <Button
            size="small"
            type={matrixMode === 'service' ? 'primary' : 'default'}
            onClick={() => setMatrixMode('service')}
          >
            Service Visits
          </Button>
        </div>

        <Card size="small" title={`Zone Visit Matrix (${matrixMode === 'service' ? 'SERVICE_ENTER -> SERVICE_EXIT' : 'ENTER -> EXIT'})`}>
          <Row gutter={12} style={{ marginBottom: 8 }}>
            <Col span={6}><Statistic title="Polygon Entries" value={zoneVisitMatrix.totals.polygonEntries} /></Col>
            <Col span={6}><Statistic title="Polygon Exits" value={zoneVisitMatrix.totals.polygonExits} /></Col>
            <Col span={6}><Statistic title="Service Entries" value={zoneVisitMatrix.totals.serviceEntries} /></Col>
            <Col span={6}><Statistic title="Service Exits" value={zoneVisitMatrix.totals.serviceExits} /></Col>
          </Row>
          <Row gutter={12} style={{ marginBottom: 8 }}>
            <Col span={6}><Statistic title="Polygon Valid Visits" value={zoneVisitMatrix.totals.polygonValidVisits} /></Col>
            <Col span={6}><Statistic title="Service Valid Visits" value={zoneVisitMatrix.totals.serviceValidVisits} /></Col>
            <Col span={6}><Statistic title="Zones Entered (Mode)" value={zoneVisitMatrix.totals.zonesEntered} /></Col>
            <Col span={6}><Statistic title="Valid Visits (Mode)" value={zoneVisitMatrix.totals.validVisits} /></Col>
          </Row>
          <Table
            size="small"
            dataSource={zoneVisitMatrix.rows}
            pagination={{ pageSize: 10 }}
            locale={{
              emptyText: (
                <div style={{ padding: 12 }}>
                  <div>No valid zone visits for this asset/date.</div>
                  <div style={{ color: '#8c8c8c', marginTop: 4 }}>
                    Check zone config and that the asset remained in {matrixMode === 'service' ? 'service radius' : 'polygon'} for at least {zoneVisitMatrix.thresholdSec}s.
                  </div>
                </div>
              ),
            }}
            columns={[
              { title: 'Zone', dataIndex: 'zoneName', key: 'zoneName' },
              { title: 'Entry Time', dataIndex: 'enterTs', key: 'enterTs', width: 180, render: (v) => moment(v).format('YYYY-MM-DD HH:mm:ss') },
              { title: 'Exit Time', dataIndex: 'exitTs', key: 'exitTs', width: 180, render: (v) => moment(v).format('YYYY-MM-DD HH:mm:ss') },
              { title: 'Time Spent', dataIndex: 'durationSec', key: 'durationSec', width: 130, render: (v) => formatDuration(v) },
              { title: 'Seconds', dataIndex: 'durationSec', key: 'durationSecNum', width: 100 },
            ]}
          />
        </Card>

        <Card
          size="small"
          style={{ marginTop: 12 }}
          title="Matched Flights for Asset/Date"
        >
          <Table
            size="small"
            pagination={{ pageSize: 5 }}
            dataSource={matchedFlightsForDrawerAsset.map((item, idx) => ({ ...item, key: `${item.flightId}_${idx}` }))}
            locale={{ emptyText: 'No matched flights for this asset/date.' }}
            columns={[
              { title: 'ARR Flight', dataIndex: 'arrFlightNo', key: 'arrFlightNo', width: 120 },
              { title: 'DEP Flight', dataIndex: 'depFlightNo', key: 'depFlightNo', width: 120 },
              { title: 'Bay(s)', key: 'bayKeys', render: (_, row) => (row.bayKeys || []).join(', ') || '-' },
              { title: 'Channel', dataIndex: 'matchChannel', key: 'matchChannel', width: 100 },
              { title: 'Zone', dataIndex: 'matchedZoneName', key: 'matchedZoneName' },
              { title: 'Overlap', dataIndex: 'overlapSec', key: 'overlapSec', width: 100, render: (v) => `${Number(v || 0)}s` },
            ]}
          />
        </Card>

        <Divider />
        <Row gutter={12}>
          <Col xs={24} lg={12}>
            <Card
              size="small"
              title="Zone + Movement Map (OpenStreetMap)"
              extra={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Button
                    size="small"
                    onClick={() => setIsMapPlaying((p) => !p)}
                    disabled={mapPlaybackPoints.length <= 1}
                  >
                    {isMapPlaying ? 'Pause' : 'Play'}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setPlaybackIndex(0);
                      setIsMapPlaying(false);
                    }}
                    disabled={mapPlaybackPoints.length <= 1}
                  >
                    Reset
                  </Button>
                </div>
              }
            >
              {trackLoading ? (
                <div style={{ textAlign: 'center', padding: 18 }}><Spin /></div>
              ) : (
                <>
                  <Row gutter={16} style={{ marginBottom: 8 }}>
                    <Col span={10}>
                      <Text strong>Selected Event:</Text>{' '}
                      <Text>
                        {selectedRangeEvents
                          ? `Range: ${moment(selectedRangeEvents.startTs).format('HH:mm:ss')} -> ${moment(selectedRangeEvents.endTs).format('HH:mm:ss')}`
                          : (
                            selectedMapEvent
                              ? `${selectedMapEvent.eventType} | ${selectedMapEvent.zoneName || selectedMapEvent.zoneId || '-'} | ${moment(selectedMapEvent.startTs).format('HH:mm:ss')}`
                              : 'None'
                          )}
                      </Text>
                    </Col>
                    <Col span={8}>
                      <Text strong>Playback Points:</Text>{' '}
                      <Text>{mapPlaybackPoints.length}</Text>
                    </Col>
                    <Col span={6}>
                      <Text strong>Speed (x):</Text>
                      <Slider
                        min={0.5}
                        max={100}
                        step={0.5}
                        value={playbackSpeed}
                        onChange={(v) => setPlaybackSpeed(Number(v || 1.5))}
                        tipFormatter={(v) => `${Number(v).toFixed(1)}x`}
                      />
                    </Col>
                  </Row>

                  <Row gutter={8} style={{ marginBottom: 10 }} type="flex" align="middle">
                    <Col span={18}>
                      <Text type="secondary">
                        Use Start/End buttons in Raw Event Timeline rows, then click Play.
                      </Text>
                    </Col>
                    <Col span={6}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setRangeStartEventId(null);
                            setRangeEndEventId(null);
                            setIsMapPlaying(false);
                          }}
                        >
                          Clear Range
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <div style={{ height: 520, border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                    <LeafletMap
                      ref={mapRef}
                      center={[23.0225, 72.5714]}
                      zoom={14}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />

                      {(selectedTemplate?.zones || []).map((zone) => {
                        const latlngs = (zone.coordinates || []).map((p) => [Number(p.lat), Number(p.lng)]);
                        const centroid = polygonCentroid(zone.coordinates || []);
                        return (
                          <React.Fragment key={zone.id}>
                            {latlngs.length >= 3 && (
                              <Polygon
                                positions={latlngs}
                                color={selectedMapEvent?.zoneId === zone.id ? '#fa8c16' : '#2f54eb'}
                                weight={selectedMapEvent?.zoneId === zone.id ? 3 : 2}
                                fillOpacity={0.12}
                              />
                            )}
                            {!!selectedTemplate?.rules?.proximityEnabled && centroid && (
                              <Circle
                                center={[centroid.lat, centroid.lng]}
                                radius={currentServiceCutoffMeters}
                                color="#13c2c2"
                                fillOpacity={0.06}
                                weight={1}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}

                      {mapPlaybackPoints.length >= 2 && (
                        <Polyline
                          positions={mapPlaybackPoints.map((p) => [p.lat, p.lng])}
                          color="#722ed1"
                          weight={2}
                          opacity={0.7}
                        />
                      )}

                      {activePlaybackPoint && (
                        <Marker
                          position={[activePlaybackPoint.lat, activePlaybackPoint.lng]}
                          icon={playbackPinIcon}
                        >
                          <Tooltip
                            permanent
                            direction="top"
                            offset={[0, -18]}
                            opacity={0.95}
                          >
                            <span style={{ fontSize: 11, fontWeight: 600 }}>{activePlaybackTimeLabel}</span>
                          </Tooltip>
                        </Marker>
                      )}
                    </LeafletMap>
                  </div>
                </>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              size="small"
              title={(
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Title level={5} style={{ margin: 0 }}>Raw Event Timeline</Title>
                  <Text type="secondary">Channel:</Text>
                  <Button
                    size="small"
                    type={eventChannelFilter === 'all' ? 'primary' : 'default'}
                    onClick={() => setEventChannelFilter('all')}
                  >
                    All
                  </Button>
                  <Button
                    size="small"
                    type={eventChannelFilter === 'polygon' ? 'primary' : 'default'}
                    onClick={() => setEventChannelFilter('polygon')}
                  >
                    Polygon
                  </Button>
                  <Button
                    size="small"
                    type={eventChannelFilter === 'service' ? 'primary' : 'default'}
                    onClick={() => setEventChannelFilter('service')}
                  >
                    Service
                  </Button>
                  <Button
                    size="small"
                    type={eventChannelFilter === 'gaps' ? 'primary' : 'default'}
                    onClick={() => setEventChannelFilter('gaps')}
                  >
                    Gaps
                  </Button>
                  <Text type="secondary">Type:</Text>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="All types"
                    style={{ minWidth: 240 }}
                    value={eventTypeFilter}
                    onChange={(vals) => setEventTypeFilter(vals || [])}
                  >
                    {availableEventTypes.map((type) => (
                      <Option key={type} value={type}>{type}</Option>
                    ))}
                  </Select>
                </div>
              )}
            >
              <Table
                loading={eventsLoading}
                size="small"
                columns={eventColumns}
                dataSource={filteredEventsData.map((event) => ({ ...event, key: event.id }))}
                onRow={(record) => {
                  const isAnimated = currentAnimatedEventId === record.id;
                  const isStart = rangeStartEventId && record.id === rangeStartEventId;
                  const isEnd = rangeEndEventId && record.id === rangeEndEventId;
                  const inRange = !!selectedRangeEvents
                    && Number(record.startTs || 0) >= selectedRangeEvents.startTs
                    && Number(record.startTs || 0) <= selectedRangeEvents.endTs;

                  let background = undefined;
                  if (isAnimated) background = '#fff1b8';
                  else if (isStart || isEnd) background = '#bae7ff';
                  else if (inRange) background = '#e6f7ff';
                  else if (selectedMapEvent?.id === record.id) background = '#f0f5ff';

                  return {
                    onClick: () => {
                      if (record.zoneId) {
                        setRangeStartEventId(null);
                        setRangeEndEventId(null);
                        setSelectedMapEvent(record);
                      }
                    },
                    style: background ? { background, transition: 'background 0.2s ease' } : undefined,
                  };
                }}
                pagination={{ pageSize: 20 }}
                scroll={{ x: 960, y: 540 }}
              />
            </Card>
          </Col>
        </Row>
      </Drawer>

      <Modal
        visible={zoneMapModalVisible}
        onCancel={() => setZoneMapModalVisible(false)}
        footer={null}
        width={900}
        title={`Geofence Map - ${zoneMapPreviewTitle}`}
      >
        {zoneMapPreviewZones.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message="No zones to display"
            description="Add or import at least one polygon geofence to preview it on the map."
          />
        ) : (
          <div style={{ height: 520, border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
            <LeafletMap
              ref={zoneMapRef}
              center={[23.0225, 72.5714]}
              zoom={14}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {zoneMapPreviewZones.map((zone) => {
                const latlngs = (zone.coordinates || []).map((p) => [Number(p.lat), Number(p.lng)]);
                const centroid = polygonCentroid(zone.coordinates || []);
                if (latlngs.length < 3) return null;
                return (
                  <React.Fragment key={zone.id}>
                    <Polygon
                      positions={latlngs}
                      color={zone.isBase ? '#1890ff' : '#2f54eb'}
                      weight={zone.isBase ? 3 : 2}
                      fillOpacity={0.14}
                    />
                    {centroid && (
                      <Marker position={[centroid.lat, centroid.lng]}>
                        <Tooltip direction="top" opacity={0.95}>
                          {zone.name}
                        </Tooltip>
                      </Marker>
                    )}
                  </React.Fragment>
                );
              })}
            </LeafletMap>
          </div>
        )}
      </Modal>

      <Modal
        visible={zoneModalVisible}
        onCancel={() => setZoneModalVisible(false)}
        onOk={addZone}
        title="Add Polygon Zone"
      >
        <div style={{ marginBottom: 10 }}>
          <Text strong>Zone Name</Text>
          <Input value={newZone.name} onChange={(e) => setNewZone({ ...newZone, name: e.target.value })} />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={newZone.isBase}
              onChange={(e) => setNewZone({ ...newZone, isBase: e.target.checked })}
            />
            Base Zone
          </label>
        </div>

        <div>
          <Text strong>Coordinates (lat,lng per line)</Text>
          <TextArea
            rows={6}
            placeholder={'26.103,91.813\n26.104,91.815\n26.101,91.816'}
            value={newZone.coordinatesText}
            onChange={(e) => setNewZone({ ...newZone, coordinatesText: e.target.value })}
          />
        </div>
      </Modal>

      <Modal
        visible={metricModalVisible}
        onCancel={() => setMetricModalVisible(false)}
        onOk={addMetric}
        title="Add Metric"
      >
        <div style={{ marginBottom: 10 }}>
          <Text strong>Metric Label</Text>
          <Input value={newMetric.label} onChange={(e) => setNewMetric({ ...newMetric, label: e.target.value })} />
        </div>

        <div style={{ marginBottom: 10 }}>
          <Text strong>Metric Type</Text>
          <Select
            style={{ width: '100%' }}
            value={newMetric.type}
            onChange={(value) => setNewMetric({ ...newMetric, type: value })}
          >
            {METRIC_TYPES.map((type) => (
              <Option key={type.value} value={type.value}>{type.label}</Option>
            ))}
          </Select>
        </div>

        {(newMetric.type === 'trip_count' || newMetric.type === 'round_trip_count') && (
          <Row gutter={8}>
            <Col span={12}>
              <Text strong>Source Zone</Text>
              <Select
                style={{ width: '100%' }}
                value={newMetric.sourceZoneId}
                onChange={(value) => setNewMetric({ ...newMetric, sourceZoneId: value })}
              >
                {(templateDraft?.zones || []).map((zone) => (
                  <Option key={zone.id} value={zone.id}>{zone.name}</Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <Text strong>Destination Zone</Text>
              <Select
                style={{ width: '100%' }}
                value={newMetric.destinationZoneId}
                onChange={(value) => setNewMetric({ ...newMetric, destinationZoneId: value })}
              >
                {(templateDraft?.zones || []).map((zone) => (
                  <Option key={zone.id} value={zone.id}>{zone.name}</Option>
                ))}
              </Select>
            </Col>
          </Row>
        )}

        {['zone_entries', 'zone_exits', 'reentry_count', 'time_inside'].includes(newMetric.type) && (
          <div style={{ marginTop: 10 }}>
            <Text strong>Zone</Text>
            <Select
              style={{ width: '100%' }}
              value={newMetric.zoneId}
              onChange={(value) => setNewMetric({ ...newMetric, zoneId: value })}
            >
              {(templateDraft?.zones || []).map((zone) => (
                <Option key={zone.id} value={zone.id}>{zone.name}</Option>
              ))}
            </Select>
          </div>
        )}

        {['time_outside_base', 'base_to_multi_poi_visits'].includes(newMetric.type) && (
          <div style={{ marginTop: 10 }}>
            <Text strong>Base Zone</Text>
            <Select
              style={{ width: '100%' }}
              value={newMetric.baseZoneId}
              onChange={(value) => setNewMetric({ ...newMetric, baseZoneId: value })}
            >
              {(templateDraft?.zones || []).map((zone) => (
                <Option key={zone.id} value={zone.id}>{zone.name}</Option>
              ))}
            </Select>
          </div>
        )}
      </Modal>

      <Modal
        visible={geoImportModalVisible}
        onCancel={() => {
          setGeoImportModalVisible(false);
          setGeoImportFile(null);
        }}
        onOk={importGeoJsonZones}
        confirmLoading={geoImportLoading}
        title="Import GeoJSON Geofences"
      >
        <div style={{ marginBottom: 10 }}>
          <Text strong>GeoJSON File (optional)</Text>
          <Upload
            accept=".geojson,.json,application/geo+json,application/json"
            beforeUpload={(file) => {
              const lowerName = `${file?.name || ''}`.toLowerCase();
              const isGeoJsonLike = lowerName.endsWith('.geojson') || lowerName.endsWith('.json');
              if (!isGeoJsonLike) {
                message.error('Please choose a .geojson or .json file');
                return Upload.LIST_IGNORE;
              }
              setGeoImportFile(file);
              return false;
            }}
            onRemove={() => setGeoImportFile(null)}
            fileList={geoImportFile ? [{
              uid: geoImportFile.uid || 'geojson-upload',
              name: geoImportFile.name || 'geojson',
              status: 'done',
            }] : []}
          >
            <Button icon="upload">Choose GeoJSON File</Button>
          </Upload>
        </div>

        <div style={{ marginBottom: 10 }}>
          <Text strong>GeoJSON URL (optional)</Text>
          <Input
            value={geoImportUrl}
            onChange={(e) => setGeoImportUrl(e.target.value)}
            placeholder="https://example.com/airport.geojson"
          />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={geoImportAsBase}
              onChange={(e) => setGeoImportAsBase(e.target.checked)}
            />
            Set first imported zone as Base Zone
          </label>
        </div>

        <Alert
          type="info"
          showIcon
          message="Supported types"
          description="Polygon and MultiPolygon features are supported. Coordinates are converted from [lng, lat] to {lat, lng}."
        />
      </Modal>
    </div>
  );
}

export default AssetUtilizationStudio;
